import React, { FC, useEffect, useRef, useState } from "react";
import { CellChange } from "handsontable/common";
import { ImageScale, IRefDocTableComponent, ITableComponentCell, SWTypes } from "interfaces/sw/SWInterfaces";
import { Settings as GetMergedCellSettings } from "handsontable/plugins/mergeCells";
import { DetailedSettings as ContextMenuSettings } from "handsontable/plugins/contextMenu";
import { CellRange } from "handsontable";
import './RefDocTableEditor.scoped.scss';
import "handsontable/dist/handsontable.full.min.css"
import { useDispatch } from "react-redux";
import {
  getRefDocTableCellImageUrls,
  setRefDocLabel,
  setRefDocTableData,
  updateTableWidthsRefDocs
} from "store/manageSW/manageSWActions";
import { AddTimeImageTargets, ImageDataDestinations, } from "store/manageSW/manageSWTypes";
import CustomCellEditorComponent from "../steps/components/CustomCellEditorComponent";
import { HotTable } from "@handsontable/react";
import MergeCellsPopUpModal from "../steps/components/MergeCellsPopUpModal";
import useSelector from "../../../../store/useSelector";
import { cloneDeep } from "lodash";
import ChangeEnhancedTableCellsFontPopUpModal from "../steps/components/ChangeEnhancedTableCellsFontPopUpModal";
import { showErrorToast } from "store/toast/toastActions";

interface IRefDocTableProps {
  refDocGuid: string,
  label: string,
  tableComponent: IRefDocTableComponent,
  allowEdit: boolean,
  swGuid: string,
  swType: SWTypes,
  swVersion: number,
  imageDataDestination: ImageDataDestinations,
  enableTimeUpload: boolean
}

const RefDocTableEditor: FC<IRefDocTableProps> = ({
  refDocGuid,
  label,
  tableComponent,
  allowEdit,
  swGuid,
  swType,
  swVersion,
  imageDataDestination,
  enableTimeUpload
}) => {
  const maxRowCount: number = useSelector(store => store.manageSW.maxRowCountForTableComponent);
  const maxColCount: number = 10;
  const dispatch = useDispatch();
  const { timeImageData } = useSelector(store => store.manageSW);
  const hotTableRef = useRef<any>(null);
  const [colWidths, setColWidths] = useState<number[]>([]);
  const [displayMergePopUp, setDisplayMergePopUp] = useState<boolean>(false);
  const mergePopUpRef = useRef<boolean>(false);
  const pendingRowRemovalsRef = useRef<Array<{ index: number, amount: number }>>([]);
  const rowRemovalFlushScheduledRef = useRef<boolean>(false);
  const pendingColRemovalsRef = useRef<Array<{ index: number, amount: number }>>([]);
  const colRemovalFlushScheduledRef = useRef<boolean>(false);
  let imageChange: boolean = false;
  let fontChange: boolean = false;
  const [displayFontSizeChangePopUp, setDisplayFontSizeChangePopUp] = useState<boolean>(false);

  const setCells = (newCells: ITableComponentCell[], newColWidths: number[]) => {
    dispatch(setRefDocTableData({
      refDocGuid: refDocGuid,
      component: {
        ...tableComponent,
        cells: newCells,
        colWidths: newColWidths
      }
    }));
  };

  const handleImageUpload = (fileName: string, newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      cell.imageFileName = fileName;
      cell.scale = scaleOption;
      setCells(newCells, colWidths);
    }
  };

  const updateCellAfterDeleteImg = (newHtmlContent: string, rowIndex: number, colIndex: number) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      setCells(newCells, colWidths);
    }
  };

  const setNewColumnWidths = (isDirty: boolean) => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const instanceWidth = hotInstance?.rootElement?.offsetWidth;
    if (instanceWidth) {
      const columns = hotInstance.countSourceCols();
      const newWidth = instanceWidth / columns;
      setColWidths(new Array(columns).fill(newWidth));
      dispatch(
        updateTableWidthsRefDocs({
          guid: refDocGuid,
          widths: new Array(columns).fill(newWidth),
          isDistry: true
        })
      )
      return new Array(columns).fill(newWidth);
    }
    return [];
  }

  useEffect(() => {
    let newCells: ITableComponentCell[] = [];
    let rows: number = 0;
    let cols: number = 0;
    let newColWidths = [];

    if (tableComponent.cells.length === 0) {
      newColWidths = setNewColumnWidths(false)
    } else if (tableComponent.colWidths !== undefined) {
      setColWidths(tableComponent.colWidths)
      newColWidths = tableComponent.colWidths;
    } else {
      newColWidths = setNewColumnWidths(false);
    }

    var colCount = tableComponent.cells.length === 0 ? tableComponent.colCount : Math.max(...tableComponent.cells.map(o => o.colIndex)) + 1;
    var rowCount = tableComponent.cells.length === 0 ? tableComponent.rowCount : Math.max(...tableComponent.cells.map(o => o.rowIndex)) + 1;

    while (rows < rowCount) {
      cols = 0;
      while (cols < colCount) {
        let cell: ITableComponentCell[] = tableComponent.cells.filter(c => c.rowIndex === rows && c.colIndex === cols);
        if (cell[0] === undefined) {
          newCells.push({
            colIndex: cols,
            rowIndex: rows,
            colSpan: 1,
            rowSpan: 1,
            value: ""
          })
          cols++;
        } else {
          newCells.push(cell[0]);
          cols = cols + cell[0].colSpan
        }
      }
      rows++;
    }
    setCells(newCells, newColWidths)
  }, [])
  
  useEffect(() => {
    if (!hotTableRef.current) {
      return;
    }

    const newColCount: number = hotTableRef.current.hotInstance.countSourceCols();
    const newRowcount: number = hotTableRef.current.hotInstance.countSourceRows();

    if (
      newColCount !== tableComponent.colCount ||
      newRowcount !== tableComponent.rowCount
    ) {
      dispatch(setRefDocTableData({
        refDocGuid: refDocGuid,
        component: {
          ...tableComponent,
          rowCount: newRowcount,
          colCount: newColCount,
        }
      }));
    }
  }, [
    hotTableRef.current,
    hotTableRef.current?.hotInstance.countSourceCols(),
    hotTableRef.current?.hotInstance.countSourceRows(),
  ]);
  
  useEffect(() => {
    for (const cell of tableComponent.cells) {
      const imgSrc = getImage(cell);

      if (imgSrc) {
        dispatch(
          getRefDocTableCellImageUrls({
            refDocGuid: refDocGuid,
            rowIndex: cell.rowIndex,
            colIndex: cell.colIndex,
            swGuid,
            swVersion,
            filename: imgSrc,
            destination: imageDataDestination,
          })
        );
      }
    }
  }, [tableComponent.cells]);

  useEffect(() => {
    if (tableComponent.colWidths !== undefined) {
      setColWidths(tableComponent.colWidths);
    } else {
      setNewColumnWidths(false)
    }
  }, [])

  const getMergedCells = (): GetMergedCellSettings => {
    return {
      virtualized: true,
      cells: tableComponent.cells
        .filter((x) => x.colSpan > 1 || x.rowSpan > 1)
        .map((x) => ({
          row: x.rowIndex,
          col: x.colIndex,
          rowspan: x.rowSpan,
          colspan: x.colSpan
        }))
    };

  };

  useEffect(() => {
    if (!hotTableRef.current) {
      return;
    }

    const hot = hotTableRef.current?.hotInstance;
    hot.batch(() => {
      tableComponent.cells.forEach((x: any) => {
        const physicalRow = hot.toPhysicalRow(x.rowIndex);
        const physicalCol = hot.toPhysicalColumn(x.colIndex);

        if (
          Number.isInteger(physicalRow) &&
          physicalRow >= 0 &&
          Number.isInteger(physicalCol) &&
          physicalCol >= 0
        ) {
          hot.setSourceDataAtCell(physicalRow, physicalCol, x.value);
        }
      });
    });

    setTimeout(() => {
      hot.render();
    }, 100);
  }, [tableComponent.cells, hotTableRef.current]);

  const handleCellChanges = (changes: CellChange[] | null) => {
    if (!changes) {
      return;
    }

    let anyChanges = false;

    const newCells = tableComponent.cells.map((x) => ({ ...x }));

    changes.forEach(([row, col, oldValue, newValue]) => {
      if (oldValue !== newValue) {
        anyChanges = true;
      }

      let cell = newCells.find((x) => x.rowIndex === row && x.colIndex === col);

      if (cell) {
        cell.value = newValue;
        if (cell.scale === undefined && typeof newValue === "string" && newValue !== null && newValue !== "" && newValue !== undefined) {
          const scaleMatch = newValue.match(/data-scale=["']([^"']+)["']/i);
          if(scaleMatch?.[1] !== undefined){
            cell.scale = scaleMatch[1] as ImageScale;
          }
        }
      } else if (typeof row === "number" && typeof col === "number") {
        cell = {
          rowIndex: row,
          colIndex: col,
          value: newValue,
          colSpan: 1,
          rowSpan: 1,
        };
        newCells.push(cell);
      }
    });

    if (anyChanges) {
      const newWidths = updateColWidths();
      setColWidths(newWidths)
      setCells(newCells, newWidths);
    }
  };

  const contextMenuSettings: ContextMenuSettings = {
    items: {
      row_above: {
        disabled: !allowEdit,
      },
      row_below: {
        disabled: !allowEdit,
      },
      col_left: {
        disabled: !allowEdit,
      },
      col_right: {
        disabled: !allowEdit,
      },
      remove_row: {
        disabled: !allowEdit,
      },
      remove_col: {
        disabled: !allowEdit,
      },
      cut: {
        disabled: !allowEdit,
      },
      copy: {
        disabled: !allowEdit,
      },
      pasteMessage: {
        name: 'To paste press Ctrl + V',
        disabled: true,
        disableSelection: true,
        isCommand: false
      },
      mergeCells: {
        disabled: !allowEdit,
      },
      changeFontSize: {
        name() {
          return 'Change Font Size for table';
        },
      }
    },
    callback: function (key) {
      if (key === 'changeFontSize') {
        setDisplayFontSizeChangePopUp(true);
      }
    },
  };

  const afterMergeCells = ({ to, from, highlight }: CellRange) => {
    if (mergePopUpRef.current) {
      setDisplayMergePopUp(true)
    }
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    const highlightCell = newCells.find(
      (x) => x.rowIndex === highlight.row && x.colIndex === highlight.col
    );

    if (!highlightCell) {
      return;
    }

    newCells = newCells.filter(
      (x) =>
        x.rowIndex < from.row ||
        x.rowIndex > to.row ||
        x.colIndex < from.col ||
        x.colIndex > to.col
    );

    const expectedRowSpan = to.row - from.row + 1;
    const expectedColSpan = to.col - from.col + 1;

    if (
      highlightCell.rowSpan === expectedRowSpan &&
      highlightCell.colSpan === expectedColSpan
    ) {
      return;
    }

    highlightCell.rowSpan = expectedRowSpan;
    highlightCell.colSpan = expectedColSpan;
    highlightCell.rowIndex = from.row;
    highlightCell.colIndex = from.col;
    newCells.push(highlightCell);
    setCells(newCells, colWidths);
  };

  const afterUnmergeCells = ({ from }: CellRange) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    const firstCell = newCells.find(
      (x) => x.rowIndex === from.row && x.colIndex === from.col
    );

    if (!firstCell) {
      return;
    }

    firstCell.rowSpan = 1;
    firstCell.colSpan = 1;
    setCells(newCells, colWidths);
  };

  const afterCreateCol = (index: number, amount: number) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    newCells = newCells.map((x) => {
      if (x.colIndex >= index) {
        x.colIndex += amount;
      }
      return x;
    });

    for (let i = 0; i < hotTableRef.current.hotInstance.countSourceRows(); i++) {
      const newCell: ITableComponentCell = {
        colIndex: index,
        colSpan: 1,
        rowIndex: i,
        rowSpan: 1,
        value: ""
      }

      newCells.sort((a, b) => {
        return a.colIndex - b.colIndex
      })

      newCells.push(newCell)
    }

    let newColWidths = updateColWidths()
    setCells(newCells, newColWidths);
  };

  const afterCreateRow = (index: number, amount: number) => {
    let newCells = tableComponent.cells.map((x) => ({ ...x }));

    newCells = newCells.map((x) => {
      if (x.rowIndex >= index) {
        x.rowIndex += amount;
      }
      return x;
    });

    setCells(newCells, colWidths);
  };

  const afterRemoveCol = (index: number, amount: number) => {
    pendingColRemovalsRef.current.push({ index, amount });

    if (colRemovalFlushScheduledRef.current) {
      return;
    }

    colRemovalFlushScheduledRef.current = true;

    Promise.resolve().then(() => {
      colRemovalFlushScheduledRef.current = false;

      const removals = pendingColRemovalsRef.current.splice(0);
      let newCells = tableComponent.cells.map((cell) => ({ ...cell }));

      removals.forEach(({ index: removalIndex, amount: removalAmount }) => {
        const endIndex = removalIndex + removalAmount;

        newCells = newCells
          .filter(
            (cell) => cell.colIndex < removalIndex || cell.colIndex >= endIndex
          )
          .map((cell) =>
            cell.colIndex >= endIndex
              ? { ...cell, colIndex: cell.colIndex - removalAmount }
              : cell
          );
      });

      const newColWidths = updateColWidths();
      setCells(newCells, newColWidths);
    });
  };

  const afterRemoveRow = (index: number, amount: number) => {
    pendingRowRemovalsRef.current.push({ index, amount });

    if (rowRemovalFlushScheduledRef.current) {
      return;
    }

    rowRemovalFlushScheduledRef.current = true;

    Promise.resolve().then(() => {
      rowRemovalFlushScheduledRef.current = false;

      const removals = pendingRowRemovalsRef.current.splice(0);
      let newCells = tableComponent.cells.map((cell) => ({ ...cell }));

      removals.forEach(({ index: removalIndex, amount: removalAmount }) => {
        const endIndex = removalIndex + removalAmount;

        newCells = newCells
          .filter(
            (cell) => cell.rowIndex < removalIndex || cell.rowIndex >= endIndex
          )
          .map((cell) =>
            cell.rowIndex >= endIndex
              ? { ...cell, rowIndex: cell.rowIndex - removalAmount }
              : cell
          );
      });

      setCells(newCells, colWidths);
    });
  };

  const beforeMergeCells = (cellRange: CellRange) => {
    const hot = hotTableRef.current?.hotInstance;
    if (hot && !mergePopUpRef.current) {
      const { from, to } = cellRange;

      for (let row = from.row; row <= to.row; row++) {
        for (let col = from.col; col <= to.col; col++) {
          const value = hot.getDataAtCell(row, col);
          if (value !== null && value !== "" && (from.row !== row || from.col !== col)) {
            mergePopUpRef.current = true
            break;
          }
        }
      }

    }
  }
  
  const updateColWidths = () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (hotInstance) {
      const newWidths = []
      for (let i = 0; i < hotInstance.countSourceCols(); i++) {
        newWidths.push(hotInstance.getColWidth(i))
      }
      setColWidths(newWidths)
      dispatch(
        updateTableWidthsRefDocs({
          guid: refDocGuid,
          widths: newWidths,
          isDistry: true
        })
      )
      return newWidths;
    }

    return [];
  }
  
  let onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRefDocLabel({
      refDocGuid: refDocGuid,
      label: e.target.value,
    }));
  }

  const handleFontSizeChange = (fontSize: number) => {
    fontChange = true;
    const hotInstance = hotTableRef.current?.hotInstance.getData();

    if (!!!hotInstance) {
      alert('Cells must be selected');
      return;
    }

    if (!!!fontSize || isNaN(fontSize)) {
      alert('Invalid font size');
      return;
    }
    let newCells = cloneDeep(tableComponent.cells.map((x) => ({ ...x })));

    newCells.forEach(cell => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cell.value, 'text/html');
      const body = doc.body;
      const validTags = ['P', 'SPAN', 'LI', 'EM', 'STRONG', 'U', 'INS'];

      const updateFontSize = (element: HTMLElement) => {
        element.style.fontSize = `${fontSize}px`;
      };

      // Processes list items.
      const processListItems = (liElement: HTMLElement) => {
        const hasSpan = liElement.querySelector('span');
        const hasDirectText = Array.from(liElement.childNodes).some(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
        );

        if (hasDirectText && !hasSpan) {
          liElement.innerHTML = liElement.innerHTML.replace(
            /(^|>)([^<]+)(?=<|$)/g,
            `$1<span style="font-size: ${fontSize}px;">$2</span>`
          );
        } else if (hasSpan) {
          const span = liElement.querySelector('span') as HTMLElement;
          updateFontSize(span);
        }

        liElement.querySelectorAll('em, strong, u, ins').forEach((nestedElement) => {
          const parentSpan = nestedElement.closest('span') as HTMLElement;
          if (!parentSpan) {
            const wrapperSpan = document.createElement('span');
            wrapperSpan.style.fontSize = `${fontSize}px`;
            nestedElement.parentNode?.replaceChild(wrapperSpan, nestedElement);
            wrapperSpan.appendChild(nestedElement);
          } else {
            updateFontSize(parentSpan);
          }
        });

        liElement.style.fontSize = '';
      };

      // Set the font size for all elements. List item gets an special treatment
      body.querySelectorAll('*').forEach((node) => {
        if (validTags.includes(node.tagName)) {
          if (node.tagName !== 'LI') {
            updateFontSize(node as HTMLElement);
          }
          else {
            processListItems(node as HTMLElement);
          }
        }
      });

      // Set content for invalid tag in cell
      if (!body.querySelector(validTags.map(tag => tag.toLowerCase()).join(','))) {
        body.innerHTML = `<p><span style="font-size: ${fontSize}px;">${cell.value}</span></p>`;
      }

      // update the cell value with new content
      cell.value = body.innerHTML;
    });

    setCells(newCells, colWidths);
  }

  const handleImageScaleUpdate = (newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => {
    imageChange = true;
    let newCells = tableComponent.cells.map((x) => ({ ...x }));
    let cell = newCells.find((x) => x.rowIndex === rowIndex && x.colIndex === colIndex);

    if (cell) {
      cell.value = newHtmlContent;
      cell.scale = scaleOption;
      setCells(newCells, colWidths);
    }
  };

  const handleBeforeCopy = (data: any, coords: any) => {
    for (let row = 0; row < data.length; row++) {
      for (let col = 0; col < data[row].length; col++) {
        data[row][col] = cleanHtmlTags(data[row][col])
      }
    }
  }

  const cleanHtmlTags = (html: any) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    let result: any[] = [];

    for (const child of Array.from(div.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;
        if (element.tagName === 'P') {
          result.push(element.innerHTML?.trim())
        } else if(element.tagName === "UL" || element.tagName === "OL"){
          result.push(element.outerHTML?.trim())
        } else if (element.tagName === 'IMG') {
          result.push(element.outerHTML.replace("\"=\"\"", "/"))
        } else {
          result.push(element.textContent || element.outerHTML || '')
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent?.trim();
        if (text) {
          result.push(text)
        }
      }
    }

    return result.join('\n');
  }

  const handleBeforePaste = (changes: any[][], coords: any[]) => {
    if (changes.length + coords[0].startRow > maxRowCount) {
      dispatch(showErrorToast(`Exceeded maximum row count of ${maxRowCount}.`));
      return false;
    }

    if (changes[0].length + coords[0].startCol > maxColCount) {
      dispatch(showErrorToast(`Exceeded maximum column count of ${maxColCount}.`));
      return false;
    }


    for (let i = 0; i < changes.length; i++) {
      for (let j = 0; j < changes[i].length; j++) {
        let cellContent = changes[i][j];
        let wrapped = '';
        // Convert value to string if needed
        if (cellContent == null) {
          wrapped = '';
        } else {
          wrapped = String(cellContent);
        }
        // Preserve <img ...> tags, encode all other < and >
        // Replace <img ...> tags with a placeholder
        const imgRegex = /<img[^>]*>/gi;
        let imgTags: string[] = [];
        wrapped = wrapped.replace(imgRegex, function (match) {
          imgTags.push(match);
          return `__IMG_PLACEHOLDER_${imgTags.length - 1}__`;
        });
        // Encode all < and >
        wrapped = wrapped.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        // Restore <img ...> tags from placeholders
        imgTags.forEach((img, idx) => {
          wrapped = wrapped.replace(`__IMG_PLACEHOLDER_${idx}__`, img);
        });
        wrapped = wrapped
          .replace(/&lt;(\/?strong)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?b)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?em)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?i)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?u)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ins)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?li\b[^&]*?)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ul)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?ol)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?span\b[^&]*?)&gt;/gi, "<$1>")
          .replace(/&lt;(\/?a\b[^&]*?)&gt;/gi, "<$1>")
          
        let parts = wrapped.split(/\n+/).filter(p => p.trim() !== "");
        wrapped = parts.map(p => addPTagForTextsOnly(p)).join("");
        wrapped = selfCloseImgTag(wrapped)

        // Wrap with <p>...</p> so only these tags are preserved
        changes[i][j] = wrapped;
      }
    }
  }

  const addPTagForTextsOnly = (element: string) => {
    if(element.includes("<img") || element.includes("<ul") || element.includes("<ol") || element.includes("<li")){
      return `${element}`
    } else{
      return `<p>${element}</p>`
    }
  }

  function selfCloseImgTag(html: string){
    return html.replace(/<img\b([^>]*)>/gi, (m) => {
      if(m.endsWith("/>")) return m;

      return m.replace(/>$/, " />")
    })
  }

  return (
    <div>
      <label className="mandatory controlLabel">
        Label
      </label>
      <input
        type="text"
        className="controlWide"
        value={label}
        onChange={(e) => onLabelChange(e)}
        disabled={!allowEdit}
      />
      <br />
      <br />
      <div style={{ width: "100%", overflow: "auto" }}>
        <HotTable
          ref={hotTableRef}
          colHeaders={() => ""}
          rowHeaders={() => ""}
          height="auto"
          width={"100%"}
          licenseKey="ceed5-f5fb4-6ef45-64847-ffe1b"
          colWidths={colWidths}
          renderer={"html"}
          afterChange={(changes) => {
            if (imageChange) {
              imageChange = false;
              return;
            } else if (fontChange) {
              fontChange = false;
              return;
            } else {
              handleCellChanges(changes);
            }
          }}
          mergeCells={getMergedCells()}
          contextMenu={contextMenuSettings}
          afterMergeCells={afterMergeCells}
          afterUnmergeCells={afterUnmergeCells}
          afterCreateCol={afterCreateCol}
          afterCreateRow={afterCreateRow}
          afterRemoveCol={afterRemoveCol}
          afterRemoveRow={afterRemoveRow}
          afterColumnResize={updateColWidths}
          minRows={1}
          minCols={1}
          maxRows={maxRowCount}
          maxCols={maxColCount}
          manualColumnResize={allowEdit}
          manualRowResize={allowEdit}
          startCols={tableComponent.cells.length === 0 ? tableComponent.colCount : Math.max(...tableComponent.cells.map(o => o.colIndex)) + 1}
          startRows={tableComponent.cells.length === 0 ? tableComponent.rowCount : Math.max(...tableComponent.cells.map(o => o.rowIndex)) + 1}
          readOnly={!allowEdit}
          stretchH="all"
          autoColumnSize={false}
          beforePaste={handleBeforePaste}
          beforeCopy={handleBeforeCopy}
          beforeCut={handleBeforeCopy}
          beforeMergeCells={beforeMergeCells}
        >
          <CustomCellEditorComponent
            hot-editor={true}
            hotTableRef={hotTableRef}
            handleImageUpload={handleImageUpload}
            timeImageData={timeImageData}
            enableTimeUpload={enableTimeUpload}
            handleImageScaleUpdate={handleImageScaleUpdate}
            disabled={!allowEdit}
            swGuid={swGuid}
            swVersion={swVersion}
            swType={swType}
            componentGuid={refDocGuid}
            timeImageTarget={AddTimeImageTargets.RefDocs}
            updateCellAfterDeleteImg={updateCellAfterDeleteImg}
          />
        </HotTable>
      </div>
      {displayMergePopUp &&
        <MergeCellsPopUpModal
          onClose={() => {
            mergePopUpRef.current = false;
            setDisplayMergePopUp(false)
          }}
        />
      }
      {displayFontSizeChangePopUp &&
        <ChangeEnhancedTableCellsFontPopUpModal
          onClose={() => {
            setDisplayFontSizeChangePopUp(false);
          }}
          onChangeFont={(fontSize) => {
            handleFontSizeChange(fontSize);
            setDisplayFontSizeChangePopUp(false);
          }}
          fontSize={11}
        />
      }
    </div>
  );
};

export default RefDocTableEditor;

const getImage = (cell: ITableComponentCell): string | undefined => {
  if (
    cell.imageFileName &&
    cell.imageFileName.includes("blob:https://")
  ) {
    return undefined;
  }

  const div = document.createElement("div");
  div.innerHTML = cell.value;
  const imgTag = div.getElementsByTagName("img")[0];

  if (imgTag && imgTag.getAttribute("alt") !== "undefined") {
    return imgTag.getAttribute("alt") || undefined;
  }

  return undefined;
};
